<template>
  <div class="version-history-page-component">
    <VdPage :loading="isLoading" with-background>
      <template #title>
        <div class="breadcrumb">
          <router-link :to="{ name: gameLinks.versionHistoryList }" class="breadcrumb-link">
            Version history
          </router-link>
          <span class="breadcrumb-separator">›</span>
          <span class="breadcrumb-current">#{{ targetVersion?.historySeq }}</span>
        </div>
      </template>
      <div>
        <div v-if="baseVersion && targetVersion" class="version-comparison">
          <div class="version-comparison__side">
            <span class="version-comparison__label">FROM</span>
            <span class="version-comparison__version">
              #{{ baseVersion.historySeq !== null ? baseVersion.historySeq : "" }}
              <VersionStatusBadge :version="baseVersion" />
            </span>
            <span class="version-comparison__name">{{ baseVersion.name }}</span>
          </div>
          <span class="version-comparison__arrow">→</span>
          <div class="version-comparison__side">
            <span class="version-comparison__label">TO</span>
            <span class="version-comparison__version">
              #{{ targetVersion.historySeq !== null ? targetVersion.historySeq : "" }}
              <VersionStatusBadge :version="targetVersion" />
            </span>
            <span class="version-comparison__name">{{ targetVersion.name }}</span>
          </div>
        </div>
        <div v-if="!isLoading" class="diff-container">
          <VersionDiffPanel :sections="sections" :show-dependencies="showDependencies" />
        </div>
      </div>
    </VdPage>
  </div>
</template>

<script lang="ts">
import { VdPage } from "@voodoo.io/voodoo-ui";
import { computed, defineComponent, onMounted } from "vue";

import useVersions from "@/hooks/useVersions";
import useVersionDiff from "@/hooks/useVersionDiff";
import links from "@/router/links";
import VersionStatusBadge from "@/pages/games/GameDisplay/children/versionHistory/VersionStatusBadge.vue";
import VersionDiffPanel from "@/pages/games/GameDisplay/children/versionHistory/VersionDiffPanel.vue";

export default defineComponent({
  name: "VersionHistoryPage",

  components: {
    VersionStatusBadge,
    VersionDiffPanel,
    VdPage,
  },

  props: {
    gameId: {
      type: String,
      required: true,
    },
    baseVersionId: {
      type: String,
      default: null,
    },
    targetVersionId: {
      type: String,
      default: null,
    },
    showDependencies: {
      type: Boolean,
      default: false,
    },
  },

  setup(props) {
    const { versions, fetchVersions } = useVersions(props.gameId);
    onMounted(fetchVersions);

    const targetVersion = computed(() => versions.value.find((v) => v.id === props.targetVersionId) ?? null);
    const baseVersion = computed(() => versions.value.find((v) => v.id === props.baseVersionId) ?? null);

    const { isLoading, sections } = useVersionDiff(props.gameId, props.baseVersionId, props.targetVersionId);

    return {
      isLoading,
      sections,
      targetVersion,
      baseVersion,
      gameLinks: links.games,
    };
  },
});
</script>

<style lang="scss" scoped>
.version-history-page-component {
  .version-comparison {
    display: flex;
    align-items: center;
    max-width: 800px;
    margin: 0 auto size("L");
    border: 1px solid var(--vd-input-border-color);
    border-radius: 8px;
    padding: size("M") size("L");

    &__side {
      align-self: start;
      display: flex;
      flex-direction: column;
      gap: 2px;
      flex: 1;
    }

    &__label {
      font-size: font("XS");
      font-weight: 600;
      color: var(--vd-base-placeholder-color);
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    &__version {
      font-size: font("XL");
      font-weight: 700;
      color: var(--vd-base-regular-text-color);
    }

    &__name {
      font-size: font("S");
      color: var(--vd-base-regular-text-color);
    }

    &__arrow {
      font-size: font("XL");
      color: var(--vd-base-placeholder-color);
      margin: 0 size("L");
      flex-shrink: 0;
    }
  }

  .breadcrumb {
    display: flex;
    align-items: center;
    gap: size("XS");
    margin-bottom: size("M");

    .breadcrumb-link {
      &:hover {
        text-decoration: underline;
      }
    }

    .breadcrumb-separator {
      color: var(--vd-base-placeholder-color);
    }

    .breadcrumb-current {
      color: var(--vd-base-regular-text-color);
    }
  }

  .diff-container {
    max-width: 800px;
    margin: 0 auto;
  }
}
</style>
